import React, { useState, useEffect } from 'react';
import { Mic, Phone, CheckCircle, XCircle, AlertTriangle, RefreshCw, Clock } from 'lucide-react';

interface VoiceAgentStatusWidgetProps {
  className?: string;
}

interface AgentStatus {
  id: string;
  name: string;
  status: 'active' | 'inactive' | 'training' | 'error';
  lastActive: Date;
}

const VoiceAgentStatusWidget: React.FC<VoiceAgentStatusWidgetProps> = ({ className = '' }) => {
  const [agents, setAgents] = useState<AgentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load agent data
  useEffect(() => {
    const loadAgentData = async () => {
      try {
        // In a real implementation, this would fetch data from an API
        // For now, we'll use mock data
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setAgents([
          {
            id: '1',
            name: 'Sarah',
            status: 'active',
            lastActive: new Date()
          },
          {
            id: '2',
            name: 'Michael',
            status: 'active',
            lastActive: new Date(Date.now() - 3600000)
          },
          {
            id: '3',
            name: 'Alex',
            status: 'training',
            lastActive: new Date(Date.now() - 86400000)
          },
          {
            id: '4',
            name: 'Emma',
            status: 'inactive',
            lastActive: new Date(Date.now() - 604800000)
          },
          {
            id: '5',
            name: 'David',
            status: 'error',
            lastActive: new Date(Date.now() - 172800000)
          }
        ]);
        setLoading(false);
      } catch (err) {
        console.error("Failed to load agent data:", err);
        setError("Failed to load agent status data");
        setLoading(false);
      }
    };

    loadAgentData();
  }, []);

  // Get status icon
  const getStatusIcon = (status: AgentStatus['status']) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="h-4 w-4 text-gray-400" />;
      case 'training':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  // Get status color
  const getStatusColor = (status: AgentStatus['status']) => {
    switch (status) {
      case 'active':
        return 'text-green-500';
      case 'inactive':
        return 'text-gray-500';
      case 'training':
        return 'text-blue-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  // Format time since last active
  const formatTimeSince = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 0) {
      return `${diffDay}d ago`;
    } else if (diffHour > 0) {
      return `${diffHour}h ago`;
    } else if (diffMin > 0) {
      return `${diffMin}m ago`;
    } else {
      return `${diffSec}s ago`;
    }
  };

  // Count active agents
  const activeAgents = agents.filter(agent => agent.status === 'active').length;

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-4 border border-gray-200 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <Mic className="h-4 w-4 text-primary mr-2" />
            LeadWealth.ai Voice Agents
          </h3>
        </div>
        <div className="flex justify-center py-4">
          <RefreshCw className="h-6 w-6 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-4 border border-gray-200 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-900 flex items-center">
            <Mic className="h-4 w-4 text-primary mr-2" />
            LeadWealth.ai Voice Agents
          </h3>
        </div>
        <div className="bg-red-50 p-3 rounded-md">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
            <div>
              <p className="text-sm text-red-700">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="text-sm text-red-700 underline mt-1"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm p-4 border border-gray-200 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900 flex items-center">
          <Mic className="h-4 w-4 text-primary mr-2" />
          LeadWealth.ai Voice Agents
        </h3>
        <span className="text-xs font-medium text-green-600">
          {activeAgents} active
        </span>
      </div>
      
      <div className="space-y-3">
        {agents.map((agent) => (
          <div key={agent.id} className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Mic className="h-4 w-4 text-primary" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{agent.name}</p>
                <p className="text-xs text-gray-500">
                  Last active: {formatTimeSince(agent.lastActive)}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              {getStatusIcon(agent.status)}
              <span className={`ml-1 text-xs font-medium ${getStatusColor(agent.status)} capitalize`}>
                {agent.status}
              </span>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">Total Calls Today</span>
          <span className="font-medium">24</span>
        </div>
        <div className="flex items-center justify-between text-xs mt-2">
          <span className="text-gray-500">Success Rate</span>
          <span className="font-medium">92%</span>
        </div>
      </div>
    </div>
  );
};

export default VoiceAgentStatusWidget;